/**
 * Helpers genéricos para construir PDFs de reportes — Fase 7.3
 *
 * Documentos relacionados:
 *   - DECISIONES.md #034 (stack reportes: jspdf + jspdf-autotable cliente)
 *
 * Estrategia:
 *   - `createReportePdf({ titulo, subtitulo, orientacion })` devuelve un
 *     `jsPDF` con marca ATSA aplicada en el header.
 *   - `addReporteTable(doc, columnas, rows, opts?)` agrega una tabla con
 *     `jspdf-autotable`, estilos consistentes (header azul ATSA, zebra
 *     rows, alineaciones por columna). Maneja paginado automático.
 *   - `addReporteFooter(doc)` agrega "Página X de Y" + fecha de
 *     generación al footer de TODAS las páginas (llamar al final).
 *
 * Los 7 reportes de Fase 7.3 se construyen sobre esta base — cada reporte
 * solo arma su array de columnas + rows desde sus queries específicas.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatDate, formatDateTime } from '../format';
import { brand, hexToRgb } from '../../config/appConfig';

// ── Constantes de marca ATSA (de brand.json) ─────────────────────────────────

/** RGB de brand.palette.primary.main (runtime) */
export const COLOR_PRIMARY: [number, number, number] = hexToRgb(brand.palette.primary.main);
/** RGB de brand.palette.text.primary (runtime) */
export const COLOR_TEXT: [number, number, number] = hexToRgb(brand.palette.text.primary);
/** RGB de brand.palette.text.secondary (runtime) */
export const COLOR_MUTED: [number, number, number] = hexToRgb(brand.palette.text.secondary);
/** Gris claro para zebra rows y divisores */
export const COLOR_ZEBRA: [number, number, number] = [0xf5, 0xf5, 0xf5];

// ── Layout (mm) ──────────────────────────────────────────────────────────────

export const MARGIN_X = 15;
export const MARGIN_TOP = 30; // espacio reservado para el header
export const MARGIN_BOTTOM = 15; // espacio para el footer

// ── Tipos públicos ───────────────────────────────────────────────────────────

export type Orientacion = 'portrait' | 'landscape';

export interface CreatePdfOpts {
  titulo: string;
  subtitulo?: string;
  orientacion?: Orientacion;
}

export type AlignHorizontal = 'left' | 'right' | 'center';

export interface ColumnaPdf {
  header: string;
  /** Alineación de la celda (header sigue la misma). Default: 'left' */
  align?: AlignHorizontal;
  /** Ancho relativo (0-100). Si no se pasa, autotable calcula. */
  width?: number;
  /** Formateador custom de la celda. Si no se pasa, se usa el valor crudo. */
  format?: (v: unknown) => string;
}

// ── Formatters predefinidos ──────────────────────────────────────────────────

/** Formatea moneda AR. Tolera null/undefined → "-". */
export const fmtCurrency = (v: unknown): string =>
  formatCurrency(typeof v === 'number' ? v : v == null ? null : Number(v));

/** Formatea fecha DD/MM/YYYY. */
export const fmtDate = (v: unknown): string =>
  formatDate(v as string | Date | null | undefined);

/** Convierte cualquier valor a string seguro. */
export const fmtText = (v: unknown): string =>
  v == null ? '-' : String(v);

// ── API principal ────────────────────────────────────────────────────────────

/**
 * Crea un documento PDF de reporte con el header de marca ya aplicado.
 * El header NO se redibuja automáticamente en cada página — para eso se
 * usa el `didDrawPage` interno de `addReporteTable` (lo hacemos en
 * cada página automáticamente).
 */
export function createReportePdf(opts: CreatePdfOpts): jsPDF {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: opts.orientacion ?? 'portrait',
  });
  addBrandHeader(doc, opts.titulo, opts.subtitulo);
  return doc;
}

/**
 * Dibuja el header de marca ATSA en la página actual.
 * Reusada por `createReportePdf` (primera página) y por el `didDrawPage`
 * hook de autotable (páginas siguientes cuando hay overflow).
 */
export function addBrandHeader(
  doc: jsPDF,
  titulo: string,
  subtitulo?: string,
): void {
  // Marca arriba izquierda
  doc.setTextColor(...COLOR_PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(brand.clientName, MARGIN_X, 12);

  doc.setTextColor(...COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    'Asociación de Trabajadores de la Sanidad Argentina',
    MARGIN_X,
    16,
  );

  // Título del reporte arriba derecha
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(titulo.toUpperCase(), pageWidth - MARGIN_X, 12, { align: 'right' });

  if (subtitulo) {
    doc.setTextColor(...COLOR_MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(subtitulo, pageWidth - MARGIN_X, 17, { align: 'right' });
  }

  // Línea divisoria
  doc.setDrawColor(...COLOR_PRIMARY);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, 22, pageWidth - MARGIN_X, 22);
}

/**
 * Agrega "Página X de Y" + fecha de generación al footer de TODAS las
 * páginas del documento. Llamar AL FINAL, después de haber renderizado
 * todo el contenido (necesita saber el total de páginas).
 */
export function addReporteFooter(doc: jsPDF, date: Date = new Date()): void {
  const totalPages = doc.internal.pages.length - 1; // pages[0] es null
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Línea divisoria
    doc.setDrawColor(...COLOR_MUTED);
    doc.setLineWidth(0.1);
    doc.line(
      MARGIN_X,
      pageHeight - MARGIN_BOTTOM + 2,
      pageWidth - MARGIN_X,
      pageHeight - MARGIN_BOTTOM + 2,
    );

    doc.setTextColor(...COLOR_MUTED);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Generado: ${formatDateTime(date)}`,
      MARGIN_X,
      pageHeight - MARGIN_BOTTOM + 7,
    );
    doc.text(
      `Página ${p} de ${totalPages}`,
      pageWidth - MARGIN_X,
      pageHeight - MARGIN_BOTTOM + 7,
      { align: 'right' },
    );
  }
}

/**
 * Renderiza una tabla con `jspdf-autotable` aplicando estilos consistentes
 * de marca ATSA. Maneja paginado automático: en cada página agrega el
 * header de marca arriba (via `didDrawPage`).
 *
 * @param doc      Documento PDF (de `createReportePdf`)
 * @param columnas Definición de columnas (header + align + format)
 * @param rows     Datos en bruto (array de objetos o arrays); el `format`
 *                 de cada columna decide cómo se renderiza cada celda
 * @param opts     Opciones extra: titulo/subtitulo para reflujar el header
 *                 en páginas adicionales (si se omite no se redibuja).
 */
export function addReporteTable(
  doc: jsPDF,
  columnas: ColumnaPdf[],
  rows: Array<Record<string, unknown> | unknown[]>,
  opts?: { titulo?: string; subtitulo?: string; startY?: number },
): void {
  const head = [columnas.map((c) => c.header)];

  const body = rows.map((row) =>
    columnas.map((col, idx) => {
      const raw = Array.isArray(row) ? row[idx] : row[col.header];
      return col.format ? col.format(raw) : fmtText(raw);
    }),
  );

  const columnStyles: Record<number, { halign?: AlignHorizontal; cellWidth?: number }> = {};
  columnas.forEach((c, i) => {
    const style: { halign?: AlignHorizontal; cellWidth?: number } = {};
    if (c.align) style.halign = c.align;
    if (c.width) style.cellWidth = c.width;
    if (Object.keys(style).length > 0) columnStyles[i] = style;
  });

  autoTable(doc, {
    head,
    body,
    startY: opts?.startY ?? MARGIN_TOP,
    margin: { left: MARGIN_X, right: MARGIN_X, bottom: MARGIN_BOTTOM + 5 },
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: COLOR_TEXT,
    },
    headStyles: {
      fillColor: COLOR_PRIMARY,
      textColor: 255,
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: { fillColor: COLOR_ZEBRA },
    columnStyles,
    didDrawPage: () => {
      // Re-dibujar header en cada página nueva si el caller lo pidió.
      // En la primera página ya lo dibujó createReportePdf.
      if (opts?.titulo && doc.getCurrentPageInfo().pageNumber > 1) {
        addBrandHeader(doc, opts.titulo, opts.subtitulo);
      }
    },
  });
}
